import React from 'react';
import { i18n } from 'config/locales';
import { numbersOnlyPattern } from 'constants/patterns';
import ValidationElement from '../ValidationElement';
import Text from '../Text';
import Checkbox from '../Checkbox';
import Show from '../Show';
import { setAttribute } from '../shared/setAttribute';

const digitsOnly = (value = '') => {
  let result = value;
  if (!(/^(\s*|\d+)$/.exec(value))) {
    result = value.replace(/\D/g, '');
  }
  return result;
};

export default class SSN extends ValidationElement {
  constructor(props) {
    super(props);
    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
    };
    this.update = this.update.bind(this);
    this.updateFirst = this.updateFirst.bind(this);
    this.updateMiddle = this.updateMiddle.bind(this);
    this.updateLast = this.updateLast.bind(this);
    this.updateNotApplicable = this.updateNotApplicable.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleErrorFirst = this.handleErrorFirst.bind(this);
    this.handleErrorMiddle = this.handleErrorMiddle.bind(this);
    this.handleErrorLast = this.handleErrorLast.bind(this);
    this.handleErrorNotApplicable = this.handleErrorNotApplicable.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.invalid = false;

    this.first = React.createRef();
    this.middle = React.createRef();
    this.last = React.createRef();
    this.notApplicable = React.createRef();
  }

  update(queue) {
    this.props.onUpdate({
      first: this.props.first,
      middle: this.props.middle,
      last: this.props.last,
      notApplicable: this.props.notApplicable,
      ...queue,
    });
  }

  componentDidMount() {
    this.update({
      first: this.props.first,
      middle: this.props.middle,
      last: this.props.last,
    });
  }

  updateFirst(values) {
    this.update({
      first: values.value,
    });
  }

  updateMiddle(values) {
    this.update({
      middle: values.value,
    });
  }

  updateLast(values) {
    this.update({
      last: values.value,
    });
  }

  updateNotApplicable(values) {
    // No values should be retained if the user marks this as not applicable
    this.update({
      notApplicable: values.checked,
      first: '',
      middle: '',
      last: '',
    });
  }

  handleErrorFirst(value, arr) {
    return this.handleError('first', value, arr);
  }

  handleErrorMiddle(value, arr) {
    return this.handleError('middle', value, arr);
  }

  handleErrorLast(value, arr) {
    return this.handleError('last', value, arr);
  }

  handleErrorNotApplicable(value, arr) {
    if (this.props.notApplicable) {
      // When not applicable, set other fields to valid to clear errors
      ['first', 'middle', 'last'].forEach((code) => {
        this.handleError(code, '', [
          {
            code: 'required',
            valid: true,
          },
        ]);
      });
      return [];
    }
    return this.handleError('notApplicable', value, arr);
  }

  handleError(code, value, arr) {
    const arr2 = (arr || []).map((err) => ({
      code: `ssn.${code}.${err.code}`,
      valid: err.valid,
    }));

    const requiredErrors = arr2.concat(
      this.constructor.errors.map((err) => ({
        code: `ssn.${err.code}`,
        valid: err.func('', this.props),
        uid: this.state.uid,
      }))
    );

    this.invalid = requiredErrors.some((x) => x.code === 'ssn.invalid' && x.valid === false);
    this.props.onError(value, requiredErrors);
    return arr2;
  }

  onKeyPress(e) {
    const code = e.keyCode ? e.keyCode : e.which; // permit Numbers and navigation
    const backspace = 8;
    const tabKey = 9;
    const deleteKey = 46;
    if (!((code >= 48 && code <= 57) // Numbers 0-9
      || (code >= 96 && code <= 105) // Keypad numbers 0-9
      || (code >= 35 && code <= 40) // Arrow Keys Home and End
      || (code === backspace)
      || (code === tabKey)
      || (code === deleteKey)
    )) {
      e.preventDefault();
    }
  }

  render() {
    const { first, middle, last } = this.props;
    const klass = `ssn ${this.props.className || ''} ${this.invalid ? 'usa-input-error' : ''
    }`.trim();
    const required = this.props.required && !this.props.notApplicable;
    const includeNAConfirmation = Boolean(first || middle || last);

    return (
      <div className={klass}>
        <Text
          attribute={setAttribute(this.props.attribute, 'first')}
          name="first"
          ref={this.first}
          className="first eapp-short-input"
          maxlength="3"
          pattern="^[0-9]{3}$"
          prefilter={digitsOnly}
          clipboard={false}
          value={this.props.first}
          disabled={this.props.notApplicable}
          onUpdate={this.updateFirst} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleErrorFirst} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.props.onFocus}
          tabNext={() => {
            this.props.tab(this.middle.current.text.current.input.current);
          }}
          required={required}
          autocorrect={false}
          autocapitalize={false}
          spellcheck="false"
          onKeyDown={this.onKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
          inputFilter={numbersOnlyPattern}
        />
        <Text
          attribute={setAttribute(this.props.attribute, 'middle')}
          name="middle"
          ref={this.middle}
          className="middle eapp-short-input"
          maxlength="2"
          pattern="^[0-9]{2}$"
          prefilter={digitsOnly}
          clipboard={false}
          value={this.props.middle}
          disabled={this.props.notApplicable}
          onUpdate={this.updateMiddle} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleErrorMiddle} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.props.onFocus}
          tabBack={() => {
            this.props.tab(this.first.current.text.current.input.current);
          }}
          tabNext={() => {
            this.props.tab(this.last.current.text.current.input.current);
          }}
          required={required}
          autocorrect={false}
          autocapitalize={false}
          spellcheck="false"
          onKeyDown={this.onKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
          inputFilter={numbersOnlyPattern}
        />
        <Text
          attribute={setAttribute(this.props.attribute, 'last')}
          name="last"
          ref={this.last}
          className="last eapp-short-input"
          maxlength="4"
          pattern="^[0-9]{4}$"
          prefilter={digitsOnly}
          clipboard={false}
          value={this.props.last}
          disabled={this.props.notApplicable}
          onUpdate={this.updateLast} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleErrorLast} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.props.onFocus}
          tabBack={() => {
            this.props.tab(this.middle.current.text.current.input.current);
          }}
          required={required}
          autocorrect={false}
          autocapitalize={false}
          spellcheck="false"
          onKeyDown={this.onKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
          inputFilter={numbersOnlyPattern}
        />
        <Show when={!this.props.hideNotApplicable}>
          <div className="flags">
            <Checkbox
              name="notApplicable"
              label={i18n.t('identification.ssn.label.notApplicable')}
              className="not-applicable"
              ref={this.notApplicable}
              toggle="false"
              value={this.props.notApplicable}
              checked={this.props.notApplicable}
              onUpdate={this.updateNotApplicable} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleErrorNotApplicable} // eslint-disable-line @typescript-eslint/unbound-method
              onFocus={this.props.onFocus}
              includeNAConfirmation={includeNAConfirmation}
            />
          </div>
        </Show>
      </div>
    );
  }
}

SSN.defaultProps = {
  name: 'ssn',
  value: '',
  first: '',
  middle: '',
  last: '',
  hideNotApplicable: false,
  notApplicable: false,
  focus: false,
  error: false,
  valid: false,
  tab: (input) => {
    input.focus();
  },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};

SSN.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required && props.notApplicable === false) {
        return !!props.first && !!props.middle && !!props.last;
      }
      return true;
    },
  }
];
